import React, { useEffect, useRef, useState } from "react";

// Subtle brand-green pointer companion.
// Desktop (fine pointer): an ~8px dot trails the cursor with easing; click briefly
// expands a translucent green ring. Hidden when the pointer leaves the window.
// Touch: no persistent dot — each tap spawns a small ring that expands and fades.
// Respects prefers-reduced-motion and never intercepts pointer events.
export default function PointerDot() {
  const dotRef = useRef(null);
  const ringRef = useRef(null);

  // Decide synchronously so the elements exist on the first render (refs ready).
  const [enabled] = useState(() => {
    if (typeof window === "undefined" || !window.matchMedia) return false;
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const fine = window.matchMedia("(pointer: fine)").matches;
    return fine && !reduced;
  });

  useEffect(() => {
    if (!enabled) return undefined;
    const pos = { x: -100, y: -100 };
    const cur = { x: -100, y: -100 };
    let raf;
    let visible = false;
    const dot = dotRef.current;
    const ring = ringRef.current;

    const tick = () => {
      cur.x += (pos.x - cur.x) * 0.18;
      cur.y += (pos.y - cur.y) * 0.18;
      if (dot) dot.style.transform = `translate3d(${cur.x - 4}px, ${cur.y - 4}px, 0)`;
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);

    const onMove = (e) => {
      pos.x = e.clientX;
      pos.y = e.clientY;
      if (!visible) {
        visible = true;
        if (dot) dot.style.opacity = "1";
      }
    };
    const onLeave = () => {
      visible = false;
      if (dot) dot.style.opacity = "0";
    };
    const onDown = (e) => {
      if (!ring) return;
      ring.style.left = `${e.clientX}px`;
      ring.style.top = `${e.clientY}px`;
      ring.classList.remove("pd-ring-anim");
      void ring.offsetWidth; // reflow so the animation retriggers on rapid clicks
      ring.classList.add("pd-ring-anim");
    };

    window.addEventListener("mousemove", onMove, { passive: true });
    window.addEventListener("mousedown", onDown, { passive: true });
    document.addEventListener("mouseleave", onLeave);
    window.addEventListener("blur", onLeave);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mousedown", onDown);
      document.removeEventListener("mouseleave", onLeave);
      window.removeEventListener("blur", onLeave);
    };
  }, [enabled]);

  // Touch ripple — runs on touch devices; harmless on desktop (no touchstart).
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return undefined;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return undefined;
    const onTouch = (e) => {
      const t = e.touches && e.touches[0];
      if (!t) return;
      const r = document.createElement("span");
      r.className = "pd-touch";
      r.style.left = `${t.clientX}px`;
      r.style.top = `${t.clientY}px`;
      document.body.appendChild(r);
      setTimeout(() => r.remove(), 340);
    };
    window.addEventListener("touchstart", onTouch, { passive: true });
    return () => window.removeEventListener("touchstart", onTouch);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- bind touch handler once
  }, []);

  if (!enabled) return null;
  return (
    <>
      <div ref={dotRef} className="pd-dot" aria-hidden="true" />
      <div ref={ringRef} className="pd-ring" aria-hidden="true" />
    </>
  );
}
